import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { IntegrationNoAccess } from '@/components/integration-no-access';
import { StepCard, type StepState } from '@/components/integration-step';
import { boardKeys, useBoards } from '@/hooks/use-boards';
import { formatDate, formatDateTime } from '@/lib/date';
import { extractErrorMessage } from '@/lib/errors';
import {
  jiraKeys,
  useConnectJira,
  useDisconnectJira,
  useJiraProjects,
  useJiraRun,
  useJiraRuns,
  useJiraStatus,
  useStartJiraImport,
  type JiraProjectRow,
  type JiraRun,
  type JiraRunCounts,
  type JiraStatus,
} from '@/lib/jira-import';
import { usePermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Info,
  Loader2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

const routePath = '/settings/integrations/jira' as const;
export const Route = createFileRoute(routePath)({
  component: JiraGate,
});

function JiraGate() {
  // Admin-managed integration — keep everyone without settings access out (direct URL).
  const canManage = usePermission('settings.general.manage');
  if (!canManage) return <IntegrationNoAccess />;
  return <JiraIntegrationPage />;
}

/** Sentinel Select value: import into a freshly created board named after the project. */
const CREATE_BOARD = '__create__';

const TOKEN_URL = 'https://id.atlassian.com/manage-profile/security/api-tokens';

function JiraIntegrationPage() {
  const { data: status, isLoading: statusLoading } = useJiraStatus();
  const { data: runs } = useJiraRuns();
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  const connected = status?.connected === true;

  // Same cached query the flow below reads — used here only to link a report's
  // project key to the board it landed in.
  const { data: projects } = useJiraProjects(connected);
  const boardLinkByKey = useMemo(
    () => Object.fromEntries((projects ?? []).map((p) => [p.key, p.linkedBoardId])),
    [projects],
  );

  // Runs the user dismissed because their detail endpoint kept failing. Without
  // this the adopt effect below would pick the same unreadable run straight
  // back up and the panel could never be cleared.
  const dismissedRunIds = useRef<Set<string>>(new Set());

  // A page load mid-import must resume showing progress: adopt a running run
  // found in history instead of waiting for the user to start a new one.
  useEffect(() => {
    if (activeRunId) return;
    const running = runs?.find((r) => r.status === 'running' && !dismissedRunIds.current.has(r.id));
    if (running) setActiveRunId(running.id);
  }, [runs, activeRunId]);

  // A finished run stays on screen on purpose — its report IS the outcome the
  // admin came for. Dismissing is for the stuck case: a run whose detail cannot
  // be fetched at all, which used to strand the panel on "Starting import..."
  // forever.
  const dismissActiveRun = () => {
    if (activeRunId) dismissedRunIds.current.add(activeRunId);
    setActiveRunId(null);
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-5xl space-y-6 px-6 py-8">
        <PageIntro />

        <ConnectStep status={status} isLoading={statusLoading} />

        <ImportFlow
          connected={connected}
          activeRunId={activeRunId}
          onRunStarted={setActiveRunId}
          onDismissRun={dismissActiveRun}
          runs={runs}
          boardLinkByKey={boardLinkByKey}
        />

        <HistorySection runs={runs} boardLinkByKey={boardLinkByKey} />
      </div>
    </div>
  );
}

// ─── Intro ─────────────────────────────────────────────────────────────────

function PageIntro() {
  return (
    <div className="space-y-4">
      <Link
        to="/settings"
        search={{ tab: 'integrations' }}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Integrations
      </Link>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Jira Import</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Bring your Jira projects into HyggeOS boards — re-runnable, pulls updates each time.
        </p>
      </div>

      <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2.5 sm:flex-row sm:items-center">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Before you start:</span> the only thing you
          need is a Jira API token.
        </p>
        <Button
          asChild
          variant="outline"
          size="sm"
          className="shrink-0 gap-1.5 cursor-pointer sm:ml-auto"
        >
          <a href={TOKEN_URL} target="_blank" rel="noopener noreferrer">
            Get an API token
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </Button>
      </div>
    </div>
  );
}

// ─── Step 1 · Connect ──────────────────────────────────────────────────────

function ConnectStep({ status, isLoading }: { status: JiraStatus | undefined; isLoading: boolean }) {
  const connect = useConnectJira();
  const disconnect = useDisconnectJira();
  const [siteUrl, setSiteUrl] = useState('');
  const [email, setEmail] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isLoading) {
    return (
      <StepCard step={1} title="Connect Jira" state="todo">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking connection...
        </div>
      </StepCard>
    );
  }

  // Done — collapse to a one-line summary so the page moves on to the real work.
  if (status?.connected) {
    return (
      <>
        <StepCard
          step={1}
          title="Connect Jira"
          state="done"
          description={`Connected as ${status.email} · ${status.siteUrl}`}
          action={
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer"
              disabled={disconnect.isPending}
              onClick={() => setConfirmOpen(true)}
            >
              {disconnect.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                'Disconnect'
              )}
            </Button>
          }
        />

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Disconnect Jira?</AlertDialogTitle>
              <AlertDialogDescription>
                The stored credentials are removed. Imported boards, tasks and project links are
                kept — reconnect any time to resume imports.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setConfirmOpen(false);
                  disconnect.mutate();
                }}
              >
                Disconnect
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  const canSubmit =
    siteUrl.trim().length > 0 &&
    email.trim().length > 0 &&
    apiToken.trim().length > 0 &&
    !connect.isPending;

  return (
    <StepCard
      step={1}
      title="Connect Jira"
      state="todo"
      description="Credentials are tested against Jira before being stored encrypted. The API token is never shown again."
    >
      <ol className="space-y-1 text-xs text-muted-foreground">
        <li>
          1. Open{' '}
          <a
            href={TOKEN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            id.atlassian.com → Security → API tokens
          </a>
          .
        </li>
        <li>2. Create a token and copy it.</li>
        <li>3. Paste it below, together with the Jira email you sign in with.</li>
      </ol>

      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          connect.mutate(
            { siteUrl: siteUrl.trim(), email: email.trim(), apiToken },
            // The token must never be readable back — drop it as soon as it is stored.
            { onSuccess: () => setApiToken('') },
          );
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="jira-site-url" className="text-xs font-medium text-muted-foreground">
              Site URL
            </label>
            <Input
              id="jira-site-url"
              type="url"
              placeholder="https://yourcompany.atlassian.net"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
              disabled={connect.isPending}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="jira-email" className="text-xs font-medium text-muted-foreground">
              Account email
            </label>
            <Input
              id="jira-email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={connect.isPending}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="jira-api-token" className="text-xs font-medium text-muted-foreground">
            API token
          </label>
          <Input
            id="jira-api-token"
            type="password"
            autoComplete="off"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            disabled={connect.isPending}
          />
        </div>

        {connect.isError && (
          <div className="flex items-start gap-1.5">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
            <p className="text-xs text-destructive">
              {extractErrorMessage(connect.error, 'Could not connect')}
            </p>
          </div>
        )}

        <Button type="submit" size="sm" className="gap-1.5 cursor-pointer" disabled={!canSubmit}>
          {connect.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {connect.isPending ? 'Testing connection...' : 'Test & connect'}
        </Button>
      </form>
    </StepCard>
  );
}

// ─── Steps 2 & 3 · Choose, then import ─────────────────────────────────────

function ImportFlow({
  connected,
  activeRunId,
  onRunStarted,
  onDismissRun,
  runs,
  boardLinkByKey,
}: {
  connected: boolean;
  activeRunId: string | null;
  onRunStarted: (id: string) => void;
  onDismissRun: () => void;
  runs: JiraRun[] | undefined;
  boardLinkByKey: Record<string, string | null>;
}) {
  const qc = useQueryClient();
  const { data: projects, isLoading, isError, error } = useJiraProjects(connected);
  const { data: boards } = useBoards();
  const startImport = useStartJiraImport();
  const {
    data: activeRun,
    isError: runLoadFailed,
    error: runLoadError,
  } = useJiraRun(activeRunId);

  const [checked, setChecked] = useState<Record<string, boolean> | null>(null);
  const [targets, setTargets] = useState<Record<string, string>>({});

  // Pre-check already-linked rows (re-import) once, when the list first arrives.
  // Later refetches must not wipe the user's selection.
  useEffect(() => {
    if (checked !== null || !projects) return;
    setChecked(Object.fromEntries(projects.map((p) => [p.key, p.linkedBoardId !== null])));
  }, [projects, checked]);

  // When the live run finishes, refresh history (frees the Import button) and
  // the project rows (new links + lastSynced columns).
  //
  // The trigger is "the status CHANGED to something terminal", not "it was
  // running and now isn't": a short import can already be terminal on the very
  // first poll, and the old `prev === 'running'` guard never fired for those —
  // history kept its stale `running` row, so the Import button stayed disabled
  // on "Import running..." while the panel right below it said finished.
  const prevStatusRef = useRef<JiraRun['status'] | null>(null);
  useEffect(() => {
    const cur = activeRun?.status ?? null;
    if (cur !== null && cur !== 'running' && prevStatusRef.current !== cur) {
      qc.invalidateQueries({ queryKey: jiraKeys.runs() });
      qc.invalidateQueries({ queryKey: jiraKeys.projects() });
      // An import that CREATED boards leaves them missing from every cached
      // board list — including the target dropdown on this very page.
      qc.invalidateQueries({ queryKey: boardKeys.lists() });
    }
    prevStatusRef.current = cur;
  }, [activeRun?.status, qc]);

  // A board already linked to ANY Jira project cannot be the target of another one.
  const linkedBoardIds = useMemo(
    () => new Set((projects ?? []).flatMap((p) => (p.linkedBoardId ? [p.linkedBoardId] : []))),
    [projects],
  );
  const availableBoards = useMemo(
    () => (boards ?? []).filter((b) => !linkedBoardIds.has(b.id)),
    [boards, linkedBoardIds],
  );

  // ...and neither can a board another ROW has already picked in this same
  // selection. Excluding only the already-linked ones let two rows choose the
  // same target, which the server rejects with a raw-UUID 400 after the user
  // has committed to the whole run.
  const pickedByRow = useMemo(() => {
    const owners = new Map<string, string>(); // boardId -> project key holding it
    for (const p of projects ?? []) {
      if (p.linkedBoardId) continue; // linked rows re-import; they render no Select
      const target = targets[p.key];
      if (target && target !== CREATE_BOARD) owners.set(target, p.key);
    }
    return owners;
  }, [projects, targets]);

  /**
   * The options and the value actually in effect for one row. A previously
   * picked board can disappear from `availableBoards` after a refetch (a
   * finished import just linked it), so a stale pick falls back to "create a
   * board" instead of leaving a blank Select and submitting a dead id.
   */
  const targetFor = (projectKey: string) => {
    const options = availableBoards.filter((b) => {
      const owner = pickedByRow.get(b.id);
      return owner === undefined || owner === projectKey;
    });
    const picked = targets[projectKey] ?? CREATE_BOARD;
    const value =
      picked !== CREATE_BOARD && !options.some((b) => b.id === picked) ? CREATE_BOARD : picked;
    return { options, value };
  };

  const boardNameById = useMemo(
    () => new Map((boards ?? []).map((b) => [b.id, b.name])),
    [boards],
  );

  /** What this row will actually do, in the words the admin would use. */
  const outcomeFor = (project: JiraProjectRow) => {
    if (project.linkedBoardId) return `Adds into ${project.linkedBoardName}`;
    const { value } = targetFor(project.key);
    if (value === CREATE_BOARD) return `Creates board ${project.key}`;
    return `Adds into ${boardNameById.get(value) ?? 'the selected board'}`;
  };

  const selectedRows = (projects ?? []).filter((p) => checked?.[p.key]);

  // Live from the active panel's own state AND from a 'running' row in history,
  // so a reload (or a run started elsewhere) still locks the button.
  const runLive =
    (activeRunId !== null && (!activeRun || activeRun.status === 'running')) ||
    (runs ?? []).some((r) => r.status === 'running');
  const importDisabled = runLive || startImport.isPending || selectedRows.length === 0;

  let creates = 0;
  let updates = 0;
  for (const row of selectedRows) {
    if (!row.linkedBoardId && targetFor(row.key).value === CREATE_BOARD) creates += 1;
    else updates += 1;
  }
  const planParts: string[] = [];
  if (creates > 0) planParts.push(`creates ${creates} board${creates === 1 ? '' : 's'}`);
  if (updates > 0) planParts.push(`updates ${updates}`);
  const planSummary =
    selectedRows.length === 0
      ? 'Nothing selected yet.'
      : `${selectedRows.length} project${selectedRows.length === 1 ? '' : 's'} · ${planParts.join(', ')}`;

  const handleImport = () => {
    const payload = {
      projects: selectedRows.map((r) => {
        const targetBoardId = r.linkedBoardId ? CREATE_BOARD : targetFor(r.key).value;
        return {
          key: r.key,
          ...(targetBoardId !== CREATE_BOARD ? { boardId: targetBoardId } : {}),
        };
      }),
    };
    startImport.mutate(payload, { onSuccess: (res) => onRunStarted(res.runId) });
  };

  // Step 3 opens up once there is something to import — or once a run exists,
  // so a live/finished run is never hidden behind a cleared selection.
  const step3Blocked = !connected
    ? 'Connect Jira first'
    : !activeRunId && !runLive && selectedRows.length === 0
      ? 'Select at least one project'
      : undefined;
  const runFinished = activeRun !== undefined && activeRun.status !== 'running';
  const step3State: StepState = runLive ? 'in_progress' : runFinished ? 'done' : 'todo';

  return (
    <>
      <StepCard
        step={2}
        title="Choose what to import"
        state={selectedRows.length > 0 ? 'done' : 'todo'}
        blockedReason={connected ? undefined : 'Connect Jira first'}
        description="Pick the projects to import and where each one lands. Linked projects re-import into their existing board."
      >
        {isLoading && (
          <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading projects from Jira...</span>
          </div>
        )}

        {isError && (
          <div className="flex items-start gap-1.5">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
            <p className="text-xs text-destructive">
              {extractErrorMessage(error, 'Could not load Jira projects')}
            </p>
          </div>
        )}

        {projects && projects.length === 0 && (
          <p className="py-4 text-sm text-muted-foreground">
            No projects are visible to this Jira account.
          </p>
        )}

        {projects && projects.length > 0 && (
          <div className="-mx-1 overflow-x-auto px-1">
            <table className="w-full min-w-[34rem] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="w-10 pb-2" />
                  <th className="pb-2 font-medium">Project</th>
                  <th className="pb-2 font-medium">Import into</th>
                  <th className="pb-2 font-medium">Last synced</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => {
                  const target = targetFor(p.key);
                  const isChecked = checked?.[p.key] === true;
                  return (
                    <tr key={p.id} className="border-b border-border last:border-0">
                      <td className="py-2.5 pr-2 align-top">
                        <Checkbox
                          className="mt-1.5"
                          checked={isChecked}
                          onCheckedChange={(v) =>
                            setChecked((c) => ({ ...(c ?? {}), [p.key]: v === true }))
                          }
                          aria-label={`Import ${p.key}`}
                        />
                      </td>
                      <td className="py-2.5 pr-3 align-top">
                        <div className="flex items-center gap-2 min-w-0 pt-1">
                          <span className="truncate font-medium">{p.name}</span>
                          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                            {p.key}
                          </span>
                        </div>
                      </td>
                      <td className="py-2.5 pr-3 align-top">
                        {p.linkedBoardId ? (
                          <Link
                            to="/boards"
                            search={{ board: p.linkedBoardId }}
                            className="inline-block pt-1 text-primary hover:underline"
                          >
                            {p.linkedBoardName}
                          </Link>
                        ) : (
                          <Select
                            value={target.value}
                            onValueChange={(v) => setTargets((t) => ({ ...t, [p.key]: v }))}
                          >
                            <SelectTrigger className="h-8 w-56 cursor-pointer">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={CREATE_BOARD}>
                                {`Create board "${p.key}"`}
                              </SelectItem>
                              {target.options.map((b) => (
                                <SelectItem key={b.id} value={b.id}>
                                  {b.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {/* The consequence, spelled out before they commit. */}
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {isChecked ? outcomeFor(p) : 'Not selected'}
                        </p>
                      </td>
                      <td className="py-2.5 align-top text-xs text-muted-foreground whitespace-nowrap">
                        <span className="inline-block pt-1">
                          {p.lastSyncedAt ? formatDate(p.lastSyncedAt) : 'Never'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 p-3">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="space-y-1 text-xs text-muted-foreground">
            <p>Importing never deletes anything in HyggeOS.</p>
            <p>Re-running an import pulls new and updated tickets from Jira.</p>
            <p>
              A ticket edited in HyggeOS is overwritten with Jira's version — until the migration is
              done, treat Jira as the source of truth.
            </p>
          </div>
        </div>
      </StepCard>

      <StepCard
        step={3}
        title="Import and watch"
        state={step3State}
        blockedReason={step3Blocked}
        description="Imports run in the background — you can leave this page and come back."
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">{planSummary}</p>
          <Button
            size="sm"
            className="shrink-0 gap-1.5 cursor-pointer"
            disabled={importDisabled}
            onClick={handleImport}
          >
            {runLive || startImport.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            {runLive ? 'Import running...' : 'Start import'}
          </Button>
        </div>

        {startImport.isError && (
          <div className="flex items-start gap-1.5">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
            <p className="text-xs text-destructive">
              {extractErrorMessage(startImport.error, 'Could not start the import')}
            </p>
          </div>
        )}

        {activeRunId && (
          <ActiveRunPanel
            run={activeRun}
            loadFailed={runLoadFailed}
            loadError={runLoadError}
            onDismiss={onDismissRun}
            boardLinkByKey={boardLinkByKey}
          />
        )}
      </StepCard>
    </>
  );
}

// ─── Active run ────────────────────────────────────────────────────────────

function ActiveRunPanel({
  run,
  loadFailed,
  loadError,
  onDismiss,
  boardLinkByKey,
}: {
  run: JiraRun | undefined;
  loadFailed: boolean;
  loadError: unknown;
  onDismiss: () => void;
  boardLinkByKey: Record<string, string | null>;
}) {
  if (!run) {
    // The run exists somewhere but we cannot read it. The poll keeps retrying,
    // so a blip recovers on its own; Dismiss is the escape hatch for a run that
    // is gone for good.
    if (loadFailed) {
      return (
        <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
          <div className="flex items-start gap-1.5">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
            <p className="text-xs text-destructive">
              {extractErrorMessage(loadError, 'Could not load this import run')} — still retrying.
            </p>
          </div>
          <Button variant="outline" size="sm" className="cursor-pointer" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Starting import...
      </div>
    );
  }

  const isRunning = run.status === 'running';
  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-2">
        {isRunning ? (
          <Loader2 className="h-4 w-4 animate-spin text-hygge" />
        ) : (
          <RunStatusBadge status={run.status} />
        )}
        <span className="text-sm font-medium">
          {isRunning ? 'Import in progress...' : 'Import finished'}
        </span>
        <span className="text-xs text-muted-foreground sm:ml-auto">
          Started {formatDateTime(run.startedAt)}
        </span>
      </div>
      <RunReportBody run={run} boardLinkByKey={boardLinkByKey} />
    </div>
  );
}

// ─── History ───────────────────────────────────────────────────────────────

function HistorySection({
  runs,
  boardLinkByKey,
}: {
  runs: JiraRun[] | undefined;
  boardLinkByKey: Record<string, string | null>;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Import history</CardTitle>
        <CardDescription>Recent import runs, newest first</CardDescription>
      </CardHeader>
      <CardContent>
        {!runs || runs.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            No imports yet — connect Jira and pick a project to start.
          </p>
        ) : (
          runs.map((run) => {
            const expanded = expandedId === run.id;
            const totals = Object.values(run.counts).reduce(
              (acc, c) => ({ created: acc.created + c.created, updated: acc.updated + c.updated }),
              { created: 0, updated: 0 },
            );
            return (
              <div key={run.id} className="border-b border-border py-2 last:border-0">
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : run.id)}
                  className="flex w-full cursor-pointer items-center gap-3 text-left"
                >
                  <ChevronRight
                    className={cn(
                      'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                      expanded && 'rotate-90',
                    )}
                  />
                  <RunStatusBadge status={run.status} />
                  <span className="truncate font-mono text-xs text-muted-foreground">
                    {run.projectKeys.join(', ')}
                  </span>
                  <span className="hidden text-xs text-muted-foreground sm:inline">
                    {totals.created} created · {totals.updated} updated
                  </span>
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                    {formatDateTime(run.startedAt)}
                  </span>
                </button>
                {expanded && (
                  <div className="ml-6 mt-2">
                    <RunReportBody run={run} boardLinkByKey={boardLinkByKey} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

// ─── Shared run report ─────────────────────────────────────────────────────

const RUN_BADGES: Record<JiraRun['status'], { label: string; className: string }> = {
  completed: { label: 'Completed', className: 'text-green-600 border-green-600/30 bg-green-600/5' },
  completed_with_errors: {
    label: 'Completed with errors',
    className: 'text-amber-600 border-amber-600/30 bg-amber-600/5',
  },
  failed: { label: 'Failed', className: 'text-destructive border-destructive/30 bg-destructive/5' },
  running: { label: 'Running', className: 'text-blue-500 border-blue-500/30 bg-blue-500/5' },
};

function RunStatusBadge({ status }: { status: JiraRun['status'] }) {
  // Fall back to the raw status so an unknown value never crashes the page.
  const cfg = RUN_BADGES[status] ?? { label: status, className: '' };
  return (
    <Badge variant="outline" className={cn('shrink-0 px-1.5 text-[10px]', cfg.className)}>
      {cfg.label}
    </Badge>
  );
}

/** The run's numbers as a sentence rather than a row of bare labels. */
function describeCounts(c: JiraRunCounts[string]): string {
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  return [
    `${plural(c.created, 'ticket')} created`,
    `${c.updated} updated`,
    `${c.skipped} skipped`,
    plural(c.comments, 'comment'),
    plural(c.attachments, 'file'),
  ].join(', ');
}

function RunReportBody({
  run,
  boardLinkByKey,
}: {
  run: JiraRun;
  boardLinkByKey: Record<string, string | null>;
}) {
  return (
    <div className="space-y-2">
      <div className="grid gap-1">
        {run.projectKeys.map((key) => {
          const c = run.counts[key];
          const boardId = boardLinkByKey[key];
          return (
            <div key={key} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
              {boardId ? (
                <Link
                  to="/boards"
                  search={{ board: boardId }}
                  className="shrink-0 font-mono font-medium text-primary hover:underline"
                >
                  {key}
                </Link>
              ) : (
                <span className="shrink-0 font-mono font-medium">{key}</span>
              )}
              <span className="text-muted-foreground">
                {c ? describeCounts(c) : 'waiting...'}
              </span>
            </div>
          );
        })}
      </div>
      <ReportIssueList
        items={run.warnings}
        label="warning"
        className="text-amber-700 dark:text-amber-400"
      />
      <ReportIssueList
        items={run.errors.map((e) => `${e.issueKey}: ${e.message}`)}
        label="error"
        className="text-destructive"
      />
    </div>
  );
}

/** First 10 entries visible, the rest tucked behind a <details> toggle. */
function ReportIssueList({
  items,
  label,
  className,
}: {
  items: string[];
  label: string;
  className: string;
}) {
  if (items.length === 0) return null;
  const first = items.slice(0, 10);
  const rest = items.slice(10);
  return (
    <div className={cn('space-y-0.5 text-xs', className)}>
      <p className="font-medium">
        {items.length} {label}
        {items.length === 1 ? '' : 's'}
      </p>
      <ul className="ml-4 list-disc space-y-0.5">
        {first.map((msg, i) => (
          <li key={i}>{msg}</li>
        ))}
      </ul>
      {rest.length > 0 && (
        <details>
          <summary className="cursor-pointer hover:underline">Show {rest.length} more</summary>
          <ul className="ml-4 list-disc space-y-0.5">
            {rest.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
